"use client";

import { useMemo, useRef, useState } from "react";
import axios from "axios";
import { Country, City } from "country-state-city";
import { ContactPhoneIti, type ContactPhoneItiHandle } from "@/app/contact/ContactPhoneIti";
import { SearchableSelect, type SelectOption } from "@/components/ui/SearchableSelect";
import { SUBMIT_LEAD_API_URL } from "@/lib/rem-api";
import {
  ALL_COUNTRIES,
  EMPTY_CONTACT,
  QUERY_TYPE_OPTIONS,
  type ContactFormState,
} from "@/lib/pillar-form-options";

/** Footer contact / query form. */
export function PillarContactForm() {
  const [contactForm, setContactForm] = useState<ContactFormState>(EMPTY_CONTACT);
  const [contactErrors, setContactErrors] = useState<Partial<ContactFormState>>({});
  const [contactPhoneError, setContactPhoneError] = useState<string | null>(null);
  const contactPhoneRef = useRef<ContactPhoneItiHandle>(null);
  const [contactSubmitted, setContactSubmitted] = useState(false);
  const [contactLoading, setContactLoading] = useState(false);
  const [contactApiError, setContactApiError] = useState<string | null>(null);

  // City options for the selected country
  const cityOptions = useMemo<SelectOption[]>(
    () =>
      contactForm.countryCode
        ? [
            ...new Map(
              (City.getCitiesOfCountry(contactForm.countryCode) ?? [])
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((c) => [c.name, { value: c.name, label: c.name }])
            ).values(),
          ]
        : [],
    [contactForm.countryCode]
  );

  const validateContactForm = (): boolean => {
    const e: Partial<ContactFormState> = {};
    if (!contactForm.firstName.trim()) e.firstName = "First name is required.";
    if (!contactForm.lastName.trim()) e.lastName = "Last name is required.";
    if (!contactForm.email.trim()) e.email = "Email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactForm.email))
      e.email = "Please enter a valid email address.";
    if (!contactForm.queryType) e.queryType = "Please select a query type.";
    if (!contactForm.message.trim()) e.message = "Message is required.";
    setContactErrors(e);

    setContactPhoneError(null);
    const phoneOk = contactPhoneRef.current?.validate() ?? false;
    if (!phoneOk) {
      const digits = contactPhoneRef.current?.getMobileDigits() ?? "";
      setContactPhoneError(
        !digits.length ? "Contact number is required." : "Please enter a valid phone number."
      );
    }

    return Object.keys(e).length === 0 && phoneOk;
  };

  const handleContactChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setContactForm((prev) => ({
      ...prev,
      [name]: value,
      // Reset city whenever country changes
      ...(name === "countryCode" ? { city: "" } : {}),
    }));
    if (contactErrors[name as keyof ContactFormState]) {
      setContactErrors((prev) => ({ ...prev, [name]: undefined }));
    }
    if (contactApiError) setContactApiError(null);
  };

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateContactForm()) return;

    setContactLoading(true);
    setContactApiError(null);

    try {
      const payload: Record<string, string | number> = {
        source: "website",
        contact_name: `${contactForm.firstName.trim()} ${contactForm.lastName.trim()}`,
        email: contactForm.email.trim(),
        notes: `Query Type: ${contactForm.queryType}\n\n${contactForm.message.trim()}`,
        rating: 0,
      };
      if (contactForm.countryCode) {
        const countryName = Country.getCountryByCode(contactForm.countryCode)?.name ?? contactForm.countryCode;
        payload.country = countryName;
        payload.state = contactForm.city.trim() || countryName;
      }
      if (contactForm.city.trim()) payload.city = contactForm.city.trim();

      const mobileDigits = contactPhoneRef.current?.getMobileDigits() ?? "";
      if (mobileDigits) {
        payload.mobile = mobileDigits;
        payload.phone = mobileDigits;
      }

      const response = await axios.post(SUBMIT_LEAD_API_URL, payload);
      if (response.status < 200 || response.status >= 300) {
        throw new Error(`Server responded with ${response.status}`);
      }

      setContactSubmitted(true);
      setContactForm(EMPTY_CONTACT);
      contactPhoneRef.current?.clear();
      setContactPhoneError(null);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Something went wrong. Please try again.";
      setContactApiError(message);
    } finally {
      setContactLoading(false);
    }
  };

  if (contactSubmitted) {
    return (
      <div className="contact-form">
        <div className="form-success">
          ✅ Thank you! Your query has been received. Our team will get back to you within 24 hours.
        </div>
        <button
          type="button"
          className="form-submit"
          onClick={() => setContactSubmitted(false)}
          style={{ marginTop: "16px" }}
        >
          Send Another Query
        </button>
      </div>
    );
  }

  return (
    <form className="contact-form" onSubmit={handleContactSubmit} noValidate>
      <h4>📝 Send Us Your Query</h4>
      <div className="form-row">
        <div className="form-field">
          <label>First Name *</label>
          <input
            type="text"
            name="firstName"
            placeholder="Ahmed"
            value={contactForm.firstName}
            onChange={handleContactChange}
            style={contactErrors.firstName ? { borderColor: "#f87171" } : undefined}
          />
          {contactErrors.firstName && (
            <p className="form-error-msg">{contactErrors.firstName}</p>
          )}
        </div>
        <div className="form-field">
          <label>Last Name *</label>
          <input
            type="text"
            name="lastName"
            placeholder="Al Mansouri"
            value={contactForm.lastName}
            onChange={handleContactChange}
            style={contactErrors.lastName ? { borderColor: "#f87171" } : undefined}
          />
          {contactErrors.lastName && (
            <p className="form-error-msg">{contactErrors.lastName}</p>
          )}
        </div>
      </div>
      <div className="form-field">
        <label>Email Address *</label>
        <input
          type="email"
          name="email"
          placeholder="ahmed@agency.ae"
          value={contactForm.email}
          onChange={handleContactChange}
          style={contactErrors.email ? { borderColor: "#f87171" } : undefined}
        />
        {contactErrors.email && (
          <p className="form-error-msg">{contactErrors.email}</p>
        )}
      </div>
      <div className="form-field">
        <label>WhatsApp / Phone *</label>
        <div className={`pillar-phone-wrap${contactPhoneError ? " has-error" : ""}`}>
          <ContactPhoneIti
            ref={contactPhoneRef}
            hasError={!!contactPhoneError}
            onChange={() => {
              if (contactPhoneError) setContactPhoneError(null);
              if (contactApiError) setContactApiError(null);
            }}
          />
        </div>
        {contactPhoneError && (
          <p className="form-error-msg">{contactPhoneError}</p>
        )}
      </div>
      <div className="form-row">
        <div className="form-field">
          <label htmlFor="contactCountry">Country</label>
          <SearchableSelect
            id="contactCountry"
            variant="dark"
            options={ALL_COUNTRIES}
            value={contactForm.countryCode}
            onChange={(val) => {
              setContactForm((prev) => ({ ...prev, countryCode: val, city: "" }));
              if (contactApiError) setContactApiError(null);
            }}
            placeholder="Select country"
            searchPlaceholder="Search countries…"
          />
        </div>
        <div className="form-field">
          <label htmlFor="contactCity">City</label>
          <SearchableSelect
            id="contactCity"
            variant="dark"
            options={cityOptions}
            value={contactForm.city}
            onChange={(val) => {
              setContactForm((prev) => ({ ...prev, city: val }));
              if (contactApiError) setContactApiError(null);
            }}
            placeholder={
              !contactForm.countryCode
                ? "Select country first"
                : cityOptions.length === 0
                ? "No cities available"
                : "Select city"
            }
            searchPlaceholder="Search cities…"
            disabled={!contactForm.countryCode || cityOptions.length === 0}
          />
        </div>
      </div>
      <div className="form-field">
        <label htmlFor="queryType">Query Type *</label>
        <SearchableSelect
          id="queryType"
          variant="dark"
          options={QUERY_TYPE_OPTIONS}
          value={contactForm.queryType}
          onChange={(val) => {
            setContactForm((prev) => ({ ...prev, queryType: val }));
            if (contactErrors.queryType) {
              setContactErrors((prev) => ({ ...prev, queryType: undefined }));
            }
            if (contactApiError) setContactApiError(null);
          }}
          placeholder="Select a topic…"
          searchable={false}
          hasError={!!contactErrors.queryType}
        />
        {contactErrors.queryType && (
          <p className="form-error-msg">{contactErrors.queryType}</p>
        )}
      </div>
      <div className="form-field">
        <label>Describe Your Query in Detail *</label>
        <textarea
          name="message"
          placeholder="Please share as much detail as possible so our team can help you quickly and accurately…"
          value={contactForm.message}
          onChange={handleContactChange}
          style={contactErrors.message ? { borderColor: "#f87171" } : undefined}
        />
        {contactErrors.message && (
          <p className="form-error-msg">{contactErrors.message}</p>
        )}
      </div>
      {contactApiError && (
        <div className="form-api-error">{contactApiError}</div>
      )}
      <button type="submit" className="form-submit" disabled={contactLoading}>
        {contactLoading ? (
          <>
            <span style={{
              width: "16px",
              height: "16px",
              border: "2px solid rgba(255,255,255,.4)",
              borderTopColor: "white",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              display: "inline-block",
            }} />
            Sending…
          </>
        ) : (
          "Send Query →"
        )}
      </button>
    </form>
  );
}
